import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * REV-353 — tier-0 lock waiver dialog.
 *
 * The backend (merged on develop) blocks locking an AuditSession when any
 * CriterionEvaluation is at attestation tier "unverified" (Tier 0) unless the
 * caller passes waive_tier_check=true AND a waive_tier_justification of at
 * least 20 characters, which is recorded permanently in the audit log. This
 * dialog captures that justification so the reviewer makes an explicit,
 * documented decision instead of hitting a raw HTTP 422.
 *
 * The justification is validated client-side (>=20 chars) before onConfirm is
 * called; the backend remains the source of truth and any rejection it returns
 * is surfaced via the `error` prop.
 */

const MIN_JUSTIFICATION = 20;

interface Props {
  open: boolean;
  /** The backend's explanation of the gate (carries the unverified count). */
  serverMessage?: string;
  /** A backend rejection to surface (e.g. justification still too short). */
  error?: string | null;
  /** True while the lock request is in flight. */
  submitting?: boolean;
  onConfirm: (justification: string) => void;
  onCancel: () => void;
}

export default function TierWaiverDialog({
  open,
  serverMessage,
  error,
  submitting = false,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [justification, setJustification] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleConfirm = () => {
    const trimmed = justification.trim();
    if (trimmed.length < MIN_JUSTIFICATION) {
      setValidationError(
        t(
          'audit.review.tierWaiver.tooShort',
          'Enter a justification of at least 20 characters — it is recorded in the audit log.'
        )
      );
      return;
    }
    setValidationError(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      aria-labelledby="tier-waiver-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="tier-waiver-dialog-title">
        {t('audit.review.tierWaiver.title', 'Lock with unverified criteria?')}
      </DialogTitle>
      <DialogContent>
        {serverMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {serverMessage}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'audit.review.tierWaiver.body',
            'One or more criteria are still Unverified (Tier 0). Locking now freezes them into the conformance snapshot. To proceed you must record a justification, which is stored permanently in the audit log.'
          )}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('audit.review.tierWaiver.justificationLabel', 'Waiver justification')}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          required
          disabled={submitting}
          error={Boolean(validationError)}
          helperText={
            validationError ||
            t(
              'audit.review.tierWaiver.justificationHelp',
              'Minimum 20 characters. Recorded permanently in the audit log.'
            )
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('audit.review.tierWaiver.confirm', 'Lock with waiver')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
